/**
 * @file Especialização Password do pipeline Input.
 * @module components/create/password
 * @see module:components/create/input
 */

/**
 * Configura painel de força e controle de visibilidade da senha.
 *
 * O indicador de força soma até cinco critérios: maiúscula, minúscula, dígito,
 * caractere especial e comprimento mínimo de 9. O painel existe somente durante
 * foco quando `strength` está ativo. `view:true` cria suffix clicável com ícone
 * eye/eye-slash e alterna o `type` entre `password` e `text`.
 *
 * @param {HTMLInputElement} elem Input base.
 * @param {Object} options Opções recebidas pelo componente.
 * @param {boolean} [options.strength=false] Exibe painel visual de força ao focar o campo.
 * @param {boolean} [options.strong] Alias legado que ativa `strength` quando `strength` não foi informado.
 * @param {boolean} [options.view=false] Adiciona botão de exibir/ocultar senha. `strength:true` também ativa `view`.
 * @returns {Array.<HTMLInputElement|Object>} Input e o mesmo objeto `options` normalizado.
 * @see module:components/create/input
 */
export default (elem, options) => {
  /**
   * Sincroniza posição e largura do painel de força com o input-wrapper.
   *
   * @returns {void}
   */
  const syncStrengthPanelFrame = () => {
    const fieldbox = elem.parentElement?.parentElement;
    const wrapper = elem.parentElement;
    const panel = fieldbox?.querySelector('.password-panel');
    if (!fieldbox || !wrapper || !panel) return;

    panel.style.left = `${wrapper.offsetLeft}px`;
    panel.style.width = `${wrapper.offsetWidth}px`;
  };

  if (options.strong === true && options.strength === undefined) {
    options.strength = true;
  }

  if (options.strength !== undefined && options.strength === true) {
    options.view = true;
    elem.data('strength', 'true');

    elem.addEventListener('focus', (e) => {
      if (e.currentTarget.data('strength') === 'true') {
        if (elem.parentElement.parentElement.querySelector('.password-panel') === null) {
          const labelText = (dic?.labels?.passwordStrength ?? dic?.labels?.passwordStrenght ?? 'Força da senha');
          const panel = document.createElement('div');
          const title = document.createElement('div');
          const p1 = document.createElement('div');
          const p2 = document.createElement('div');
          const p3 = document.createElement('div');
          const p4 = document.createElement('div');
          const p5 = document.createElement('div');
          const p6 = document.createElement('div');
          const p7 = document.createElement('div');

          title.innerText = labelText;
          title.classList.add('password-panel-title');
          p1.classList.add('strength-bar');
          p2.classList.add('strength-bar');
          p3.classList.add('strength-bar');
          p4.classList.add('strength-bar');
          p5.classList.add('strength-bar');
          p6.classList.add('strength-label', 'strength-label-left');
          p7.classList.add('strength-label', 'strength-label-right');
          p6.innerText = dic.labels.weakFemale;
          p7.innerText = dic.labels.strong;
          panel.append(title, p1, p2, p3, p4, p5, p6, p7);
          elem.parentElement.after(panel);
          panel.classList.add('password-panel');
          syncStrengthPanelFrame();
        }
      }

      syncStrengthPanelFrame();
      
      if (elem.parentElement.parentElement.querySelector('.password-panel') !== null) {
        const password = e.currentTarget.value;
        let values = 0;

        // Conta o número de tipos de caracteres presentes na senha
        if (/[A-Z]/.test(password)) values++;
        if (/[a-z]/.test(password)) values++;
        if (/[0-9]/.test(password)) values++;
        if (/[^A-Za-z0-9]/.test(password)) values++;
        if (password.length >= 9) values++;

        // Reseta as cores dos elementos
        const panels = document.querySelectorAll('.password-panel .strength-bar');
        panels.forEach(item => {
          item.style.backgroundColor = 'transparent';
        });

        // Definição de cores baseadas na força da senha
        const colors = ['red', 'orange', 'yellow', 'yellowgreen', 'green'];

        // Atualiza a cor de acordo com o número de critérios atendidos
        for (let i = 0; i < values; i++) {
          panels[i].style.backgroundColor = colors[i];
        }

        syncStrengthPanelFrame();
      }
    });

    elem.addEventListener('blur', (e) => {
      if (e.currentTarget.data('strength') === 'true') {
        if (elem.parentElement.parentElement.querySelector('.password-panel') !== null) {
          elem.parentElement.parentElement.querySelector('.password-panel').remove();
        }
      }
    });

    elem.addEventListener('keyup', (e) => {
      if (elem.parentElement.parentElement.querySelector('.password-panel') !== null) {
        const password = e.currentTarget.value;
        let values = 0;

        // Conta o número de tipos de caracteres presentes na senha
        if (/[A-Z]/.test(password)) values++;
        if (/[a-z]/.test(password)) values++;
        if (/[0-9]/.test(password)) values++;
        if (/[^A-Za-z0-9]/.test(password)) values++;
        if (password.length >= 9) values++;

        // Reseta as cores dos elementos
        const panels = document.querySelectorAll('.password-panel .strength-bar');
        panels.forEach(item => {
          item.style.backgroundColor = 'transparent';
        });

        // Definição de cores baseadas na força da senha
        const colors = ['red', 'orange', 'yellow', 'yellowgreen', 'green'];

        // Atualiza a cor de acordo com o número de critérios atendidos
        for (let i = 0; i < values; i++) {
          panels[i].style.backgroundColor = colors[i];
        }

        syncStrengthPanelFrame();
      }
    });
  } else {
    elem.data('strength', 'false');
  }

  if (options.view !== undefined && options.view === true) {
    options.suffix = {
      icon: { type: 'solid', name: 'eye-slash' },
      click: (e) => {
        e.preventDefault();
        const icon = e.currentTarget.querySelector('i');
        const input = e.currentTarget.previousSibling;
        if (icon.classList.contains('fa-eye-slash')) {
          icon.classList.remove('fa-eye-slash')
          icon.classList.add('fa-eye');
          input.type = 'text';
        } else {
          icon.classList.remove('fa-eye');
          icon.classList.add('fa-eye-slash');
          input.type = 'password';
        }
      }
    };
  }

  return [elem, options];
}
